import React, { ReactNode } from "react";
import { useIntl } from "react-intl";
import { msgs } from "../msgs";
import { categoryOf } from "./widget-approval/shared";
import type { ToolCategory } from "./widget-approval/shared";
import { PromotionApprovalSummary } from "./widget-approval/create-summary";
import { UpdateApprovalSummary } from "./widget-approval/update-summary";
import {
  DeleteApprovalSummary,
  MultiToggleApprovalSummary,
  MultiUpdateApprovalSummary,
  ToggleApprovalSummary,
} from "./widget-approval/toggle-and-multi";
import { MultiUpdateChainApprovalSummary } from "./widget-approval/chain-summary";

// Re-exported for backward compatibility: WidgetObject.tsx renders with the same visual language
// as these approval cards (ApprovalCard/CardHeader/Section/StatePill), and ToolLoading.tsx picks
// its label off the same tool-name → category mapping (categoryOf) the approval cards use. The
// actual implementations now live in ./widget-approval — this file is the module's orchestrator +
// public entry point, kept at its original path/name so nothing importing from "./WidgetApproval"
// needs to change.
export {
  ApprovalCard,
  CardHeader,
  MetaLabel,
  Section,
  StatePill,
  categoryOf,
} from "./widget-approval/shared";
// Kept as its own type-only export statement (rather than an inline `type` modifier inside the
// list above) — this project's pinned prettier (2.0.5) predates support for inline `export {
// type X }` specifiers and fails to parse it, even though tsc (5.9.3) compiles it fine.
export type { ToolCategory } from "./widget-approval/shared";

/**
 * The intro sentence ("Confirma la eliminación de la promoción X") is rendered as its
 * own small component so callers can place it in the TEXT lane, next to the AI avatar
 * — like any other assistant sentence — while the card + Yes/No buttons/answer (see
 * WidgetApproval below) render separately, full width, underneath.
 */
export function ApprovalMessage({
  toolType,
  input,
}: {
  toolType: string;
  input: unknown;
}): React.JSX.Element {
  const intl = useIntl();
  const toolName = toolType.replace(/^tool-/, "");
  const category = categoryOf(toolName);
  const data = input as { ticker?: string; promotions?: unknown[]; entries?: unknown[] };
  const bold = (chunks: ReactNode) => <strong className="font-semibold">{chunks}</strong>;
  const title = data.ticker ?? "";
  // Multi-ticker/multi-entry categories carry no single `ticker`, only a `promotions` (single-
  // property multi-ticker) or `entries` (chain multi-entry) array — the intro sentence for those
  // is pluralized by count instead of naming one ticker in bold.
  const count = Array.isArray(data.promotions)
    ? data.promotions.length
    : Array.isArray(data.entries)
    ? data.entries.length
    : 0;

  const message: ReactNode =
    category === "create"
      ? intl.formatMessage(msgs.confirmCreateMessage, { title, b: bold })
      : category === "delete"
      ? intl.formatMessage(msgs.confirmDeleteMessage, { title, b: bold })
      : category === "update"
      ? intl.formatMessage(msgs.confirmUpdateMessage, { title, b: bold })
      : category === "activate"
      ? intl.formatMessage(msgs.confirmActivateMessage, { title, b: bold })
      : category === "deactivate"
      ? intl.formatMessage(msgs.confirmDeactivateMessage, { title, b: bold })
      : category === "activateMultiple"
      ? intl.formatMessage(msgs.confirmActivateMultipleMessage, { count })
      : category === "deactivateMultiple"
      ? intl.formatMessage(msgs.confirmDeactivateMultipleMessage, { count })
      : category === "updateMultiple"
      ? intl.formatMessage(msgs.confirmUpdateMultipleMessage, { count })
      : category === "updateChainMultiple"
      ? intl.formatMessage(msgs.confirmUpdateChainMultipleMessage, { count })
      : intl.formatMessage(msgs.confirmGenericMessage);

  return <div className="text-sm text-[#3A3F51]">{message}</div>;
}

export interface WidgetApprovalProps {
  toolType: string;
  input: unknown;
  approval: { id: string; approved?: boolean };
  state: string;
  onApproval: (id: string, approved: boolean, category: ToolCategory) => void;
  /**
   * True when this approval belongs to a message loaded from stored history (a previous
   * session), not the live turn. A pending approval (`approval-requested`) that was never
   * answered persists in that state, so on reload it would otherwise render actionable
   * Yes/No buttons for a turn that can no longer be resumed — approving it can't execute the
   * original tool call and leaves a dangling function call. When historical, we render a
   * neutral "no longer available" note instead of the buttons.
   */
  historical?: boolean;
}

export function WidgetApproval({
  toolType,
  input,
  approval,
  state,
  onApproval,
  historical = false,
}: WidgetApprovalProps): React.JSX.Element {
  const intl = useIntl();
  const toolName = toolType.replace(/^tool-/, "");

  const category = categoryOf(toolName);
  const isCreate = category === "create";
  const isDelete = category === "delete";
  const isUpdate = category === "update";
  const isActivate = category === "activate";
  const isDeactivate = category === "deactivate";
  const isActivateMultiple = category === "activateMultiple";
  const isDeactivateMultiple = category === "deactivateMultiple";
  const isUpdateMultiple = category === "updateMultiple";
  const isUpdateChainMultiple = category === "updateChainMultiple";

  // Primary CTA label follows the action (design shows "Create promotion" for create).
  const actionLabel = isCreate
    ? intl.formatMessage(msgs.confirmCreateAction)
    : isDelete
    ? intl.formatMessage(msgs.confirmDeleteAction)
    : isUpdate
    ? intl.formatMessage(msgs.confirmUpdateAction)
    : isActivate
    ? intl.formatMessage(msgs.confirmActivateAction)
    : isDeactivate
    ? intl.formatMessage(msgs.confirmDeactivateAction)
    : isActivateMultiple
    ? intl.formatMessage(msgs.confirmActivateMultipleAction)
    : isDeactivateMultiple
    ? intl.formatMessage(msgs.confirmDeactivateMultipleAction)
    : isUpdateMultiple
    ? intl.formatMessage(msgs.confirmUpdateMultipleAction)
    : isUpdateChainMultiple
    ? intl.formatMessage(msgs.confirmUpdateChainMultipleAction)
    : intl.formatMessage(msgs.confirmGenericAction);

  return (
    <div className="my-1">
      {isCreate && <PromotionApprovalSummary input={input} />}
      {isDelete && <DeleteApprovalSummary input={input} />}
      {isUpdate && <UpdateApprovalSummary input={input} />}
      {(isActivate || isDeactivate) && (
        <ToggleApprovalSummary input={input} activate={isActivate} />
      )}
      {(isActivateMultiple || isDeactivateMultiple) && (
        <MultiToggleApprovalSummary input={input} activate={isActivateMultiple} />
      )}
      {isUpdateMultiple && <MultiUpdateApprovalSummary input={input} />}
      {isUpdateChainMultiple && <MultiUpdateChainApprovalSummary input={input} />}

      {state === "approval-requested" && historical ? (
        // A pending approval loaded from a previous session: the turn can no longer be
        // resumed, so render a neutral note instead of clickable Yes/No buttons.
        <div className="text-xs text-[#9CA3AF] mt-2">{intl.formatMessage(msgs.confirmExpired)}</div>
      ) : state === "approval-requested" ? (
        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={() => onApproval(approval.id, false, category)}
            className="rounded-[9px] border border-[#E5E7EB] bg-white px-[13.5px] py-[11.5px] text-center text-sm font-normal leading-none text-[#212121] hover:bg-gray-50">
            {intl.formatMessage(msgs.confirmCancel)}
          </button>
          <button
            type="button"
            onClick={() => onApproval(approval.id, true, category)}
            className="flex-1 flex items-center justify-center gap-2 rounded-[9px] bg-[#4D54D1] pl-[11.5px] pr-[15.5px] py-[11.5px] text-center text-sm font-normal leading-none text-white hover:brightness-110">
            <em className="fa fa-check text-white text-[13px]" aria-hidden />
            {actionLabel}
          </button>
        </div>
      ) : (
        <div className="text-xs text-[#9CA3AF] mt-2">
          {intl.formatMessage(msgs.confirmAnswered)}{" "}
          <span className="font-medium text-[#6B7280]">
            {approval.approved
              ? intl.formatMessage(msgs.confirmYes)
              : intl.formatMessage(msgs.confirmNo)}
          </span>
        </div>
      )}
    </div>
  );
}
